import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import PageHeader from '../components/common/PageHeader';
import exportRowsToXlsx from '../utils/exportXlsx';
import { getFeatures, createFeature, updateFeature, deleteFeature } from '../services/featuresApi';

const Features = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const data = await getFeatures();
      setItems(data.items);
    } catch (err) {
      console.error('Failed to fetch features:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setFormName('');
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormName(item.name);
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (editItem) {
        await updateFeature(editItem.id, { name: formName.trim() });
      } else {
        await createFeature(formName.trim());
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feature?')) return;
    setDeleting(id);
    try {
      await deleteFeature(id);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      const existingNames = new Set(items.map((i) => i.name.toLowerCase()));
      let created = 0, skipped = 0;
      for (let i = 1; i < rows.length; i++) {
        const name = String(rows[i][0] || '').trim();
        if (!name || existingNames.has(name.toLowerCase())) { skipped++; continue; }
        await createFeature(name);
        existingNames.add(name.toLowerCase());
        created++;
      }
      alert(`Import complete! ${created} created, ${skipped} skipped (empty or duplicate)`);
      await fetchData();
    } catch (err) {
      console.error('Import failed:', err);
      alert(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Features" subtitle="Manage product and fabric features (sent as tags)" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Features"
        subtitle="Manage product and fabric features (sent as tags: Features_name)"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input id="features-import-file" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleImport} />
            <Button variant="outlined" startIcon={<UploadFileRounded />}
              onClick={() => document.getElementById('features-import-file')?.click()}
              disabled={importing}>
              {importing ? 'Importing...' : 'Import Excel'}
            </Button>
            <Button variant="outlined" startIcon={<FileDownloadRounded />}
              onClick={() => exportRowsToXlsx({
                rows: items.map((i) => ({ name: i.name })),
                columns: ['name'],
                filename: 'features',
                sheetName: 'Features',
              })}
              disabled={items.length === 0}>
              Export
            </Button>
            <Button variant="contained" startIcon={<AddRounded />} onClick={openCreate}>
              Add Feature
            </Button>
          </Box>
        }
      />

      {items.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <StarRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No features added</Typography>
            <Typography variant="body2" color="text.secondary">Click "Add Feature" to create your first entry (e.g. "i Clean", "Resilience")</Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Tag</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', width: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">Features_{item.name}</Typography></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'primary.main' }}>
                            <EditRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(item.id)} disabled={deleting === item.id} sx={{ color: 'error.main' }}>
                            {deleting === item.id ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineRounded fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editItem ? 'Edit Feature' : 'Add Feature'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus label="Feature Name" placeholder="e.g. i Clean, Resilience"
            value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1 }} error={!!error} helperText={error}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editItem ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Features;
